/**
 * Enhanced context for managing tee time state throughout the application
 */
import React, { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { 
  DailyTeeTimeSchedule, 
  TeeTime, 
  Booking, 
  Player,
  GolfCourse,
  UserProfile
} from '../types';
import {
  getWeeklySchedule,
  getTeeTimesForDate,
  getAllTeeTimesForDate,
  getCourseWeeklySchedule,
  bookTeeTime,
  cancelBooking,
  getPlayerBookings
} from '../services/teeTimeService';
import {
  getAllGolfCourses,
  getGolfCourseById
} from '../services/golfCourseService';

/**
 * Interface for the TeeTime context state and methods
 */
interface TeeTimeContextType {
  // Golf course state
  golfCourses: GolfCourse[];
  selectedCourse: GolfCourse | null;
  
  // Schedule state
  weeklySchedule: DailyTeeTimeSchedule[];
  selectedDate: Date;
  selectedTeeTime: TeeTime | null;
  
  // User state
  userProfile: UserProfile | null;
  userBookings: Booking[];
  
  // UI state
  loading: boolean;
  error: string | null;
  
  // Methods
  setSelectedCourse: (course: GolfCourse | null) => void;
  setSelectedDate: (date: Date) => void;
  setSelectedTeeTime: (teeTime: TeeTime | null) => void;
  refreshSchedule: () => void;
  refreshCourseSchedule: (courseId: string) => void;
  createBooking: (
    courseId: string,
    teeTimeId: string, 
    players: Omit<Player, 'id'>[],
    additionalServices?: {
      caddies?: number;
      lessonRequested?: boolean;
      foodPreOrder?: boolean;
    }
  ) => Promise<Booking | null>;
  getUserBookings: (email: string) => void;
  cancelUserBooking: (bookingId: string) => Promise<boolean>;
  setUserProfile: (profile: UserProfile | null) => void;
}

// Create context with default values
export const TeeTimeContext = createContext<TeeTimeContextType>({
  golfCourses: [],
  selectedCourse: null,
  weeklySchedule: [],
  selectedDate: new Date(),
  selectedTeeTime: null,
  userProfile: null,
  userBookings: [],
  loading: false,
  error: null,
  setSelectedCourse: () => {},
  setSelectedDate: () => {},
  setSelectedTeeTime: () => {},
  refreshSchedule: () => {},
  refreshCourseSchedule: () => {},
  createBooking: async () => null,
  getUserBookings: () => {},
  cancelUserBooking: async () => false,
  setUserProfile: () => {},
});

interface TeeTimeProviderProps {
  children: ReactNode;
}

/**
 * Provider component for the TeeTimeContext
 * @param props Component props
 * @returns Provider component
 */
export const TeeTimeProvider: React.FC<TeeTimeProviderProps> = ({ children }) => {
  // Golf course state
  const [golfCourses, setGolfCourses] = useState<GolfCourse[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<GolfCourse | null>(null);
  
  // Schedule state
  const [weeklySchedule, setWeeklySchedule] = useState<DailyTeeTimeSchedule[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTeeTime, setSelectedTeeTime] = useState<TeeTime | null>(null);
  
  // User state
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userBookings, setUserBookings] = useState<Booking[]>([]);
  
  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Loads all available golf courses
   */
  const loadGolfCourses = useCallback(() => {
    try {
      const courses = getAllGolfCourses();
      setGolfCourses(courses);
      
      // No longer auto-selecting the first course so the gallery view will show first
    } catch (err) {
      console.error('Failed to load golf courses:', err);
      setError('Failed to load golf courses');
    }
  }, [selectedCourse]);

  /**
   * Refreshes the tee time schedule data for all courses
   */
  const refreshSchedule = useCallback(() => {
    setLoading(true);
    setError(null);
    
    try {
      const schedule = getWeeklySchedule();
      setWeeklySchedule(schedule);
    } catch (err) {
      setError('Failed to load tee time schedule');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Refreshes the tee time schedule data for a specific course
   * @param courseId ID of the golf course to refresh schedule for
   */
  const refreshCourseSchedule = useCallback((courseId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const schedule = getCourseWeeklySchedule(courseId);
      setWeeklySchedule(schedule);
    } catch (err) {
      setError('Failed to load course schedule');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Creates a booking for a tee time
   * @param courseId ID of the golf course
   * @param teeTimeId The ID of the tee time to book
   * @param players Array of players to book the tee time for
   * @param additionalServices Optional additional services for the booking
   * @returns The booking confirmation or null if booking failed
   */
  const createBooking = async (
    courseId: string,
    teeTimeId: string,
    players: Omit<Player, 'id'>[],
    additionalServices?: {
      caddies?: number;
      lessonRequested?: boolean;
      foodPreOrder?: boolean;
    }
  ): Promise<Booking | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const booking = bookTeeTime(courseId, teeTimeId, players, additionalServices);
      
      if (booking) {
        // Refresh the schedule to update availability
        if (selectedCourse?.id === courseId) {
          refreshCourseSchedule(courseId);
        } else {
          refreshSchedule();
        }
        
        // Update user bookings if this user has made the booking
        if (players.length > 0) {
          getUserBookings(players[0].email);
        }
        
        return booking;
      }
      
      setError('Failed to book tee time');
      return null;
    } catch (err) {
      setError('An error occurred while booking the tee time');
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Gets all bookings for a specific user
   * @param email The email of the user
   */
  const getUserBookings = useCallback((email: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const bookings = getPlayerBookings(email);
      setUserBookings(bookings);
    } catch (err) {
      setError('Failed to load user bookings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cancels a user's booking
   * @param bookingId The ID of the booking to cancel
   * @returns true if cancellation was successful, false otherwise
   */
  const cancelUserBooking = async (bookingId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      const success = cancelBooking(bookingId);
      
      if (success) {
        // Refresh data
        refreshSchedule();
        setUserBookings(prev => prev.filter(booking => booking.id !== bookingId));
        return true;
      }
      
      setError('Failed to cancel booking');
      return false;
    } catch (err) {
      setError('An error occurred while cancelling the booking');
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Load initial data when the component mounts
  useEffect(() => {
    loadGolfCourses();
    refreshSchedule();
  }, [loadGolfCourses, refreshSchedule]);

  // Update course schedule when selected course changes
  useEffect(() => {
    if (selectedCourse) {
      refreshCourseSchedule(selectedCourse.id);
    }
  }, [selectedCourse, refreshCourseSchedule]);

  // Update selected tee time when date changes
  useEffect(() => {
    setSelectedTeeTime(null);
  }, [selectedDate]);

  const contextValue: TeeTimeContextType = {
    golfCourses,
    selectedCourse,
    weeklySchedule,
    selectedDate,
    selectedTeeTime,
    userProfile,
    userBookings,
    loading,
    error,
    setSelectedCourse,
    setSelectedDate,
    setSelectedTeeTime,
    refreshSchedule,
    refreshCourseSchedule,
    createBooking,
    getUserBookings,
    cancelUserBooking,
    setUserProfile,
  };

  return (
    <TeeTimeContext.Provider value={contextValue}>
      {children}
    </TeeTimeContext.Provider>
  );
};
